import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeamList } from '@/api/base';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { FilterBar, FilterChip, MultiFilterChip, SearchInput } from '@/components/kit/Filters';
import { PageHeader, Segments } from '@/components/kit/Layout';
import { Who } from '@/components/kit/Person';
import { formatPhone } from '@/lib/phone';
import { ListPanel } from '../shared/ListPanel';
import { useDebouncedSearch } from '../shared/useDebouncedSearch';
import { joinList, listOf, useListParams } from '../shared/useListParams';
import { NewTeamModal } from './TeamDialogs';

const SIGNAL_TEXT = { studio_archived: 'студия в архиве', awaiting_head: 'данные ждут руководителя' };

/**
 * Список команд (БЛ 11.7, viewTeams в макете): Команда · Город · Студия ·
 * Руководитель · Участников · Внимание. «Текущее участие» появится
 * вместе с модулем «Регистрации» (ТЗ M2 7.3).
 */
export function TeamsListPage() {
  const navigate = useNavigate();
  const { values, page, set } = useListParams(['state', 'studioId', 'cityId', 'responsible', 'q'] as const);
  const state = values.state === 'archive' ? 'archive' : 'active';
  const [text, setText] = useDebouncedSearch(values.q, (q) => set({ q }));
  const [creating, setCreating] = useState(false);
  const list = useTeamList({ ...values, state, page });
  const data = list.data;
  const filtered = Boolean(values.studioId || values.cityId || values.responsible || values.q);

  const head = (
    <tr>
      <th className="c-tname srt">
        Команда <span className="ar">▲</span>
      </th>
      <th className="c-tcity">Город</th>
      <th className="c-tst">Студия</th>
      <th className="c-thead">Руководитель</th>
      <th className="r c-tnum">Участников</th>
      <th className="c-tatt">Внимание</th>
    </tr>
  );

  return (
    <div className="main">
      <PageHeader title="Команды" count={data?.segments.active}>
        <Button tone="primary" onClick={() => setCreating(true)}>
          ＋ Создать команду
        </Button>
      </PageHeader>

      <Segments
        value={state}
        onChange={(s) => set({ state: s === 'active' ? null : s, studioId: null, cityId: null, responsible: null })}
        items={[
          { value: 'active', label: 'Активные', count: data?.segments.active },
          { value: 'archive', label: 'Архив', count: data?.segments.archive },
        ]}
      />

      <FilterBar search={<SearchInput value={text} onChange={setText} placeholder="Команда, руководитель, участник, телефон" />}>
        <FilterChip
          label="Студия"
          value={values.studioId}
          onChange={(studioId) => set({ studioId })}
          options={(data?.facets.studios ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
        />
        <MultiFilterChip
          label="Город"
          values={listOf(values.cityId)}
          onChange={(v) => set({ cityId: joinList(v) })}
          options={(data?.facets.cities ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
        />
        <MultiFilterChip
          label="Ответственный"
          values={listOf(values.responsible)}
          onChange={(v) => set({ responsible: joinList(v) })}
          options={(data?.facets.responsibles ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
        />
      </FilterBar>

      <ListPanel
        head={head}
        cols={6}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        errorTitle="Не удалось загрузить команды."
        empty={
          data && !data.items.length ? (
            filtered ? (
              <>
                По этим условиям команд не нашлось.
                <br />
                <TextLink onClick={() => set({ studioId: null, cityId: null, responsible: null, q: null })}>
                  Сбросить фильтры
                </TextLink>
              </>
            ) : state === 'archive' ? (
              'В архиве команд нет.'
            ) : (
              <>
                Команд пока нет.
                <br />
                <TextLink onClick={() => setCreating(true)}>Создать команду</TextLink>
              </>
            )
          ) : null
        }
        paging={
          data
            ? { page, pageSize: data.pageSize, total: data.total, shown: data.items.length, onPage: (p) => set({ page: String(p) }) }
            : undefined
        }
        rows={data?.items.map((t) => (
          <tr key={t.id} className="clk" onClick={() => navigate(`/teams/${t.id}`)}>
            <td data-col="primary">
              <Who initials={t.initials} name={t.name} />
            </td>
            <td data-col="hide">{t.city.name}</td>
            <td data-col="hide">
              {t.studio ? (
                <>
                  {t.studio.name}
                  {t.studio.archived ? <div className="sub wtx">в архиве</div> : null}
                </>
              ) : (
                <span className="dim">без студии</span>
              )}
            </td>
            <td data-col="hide">
              {t.head.shortName}
              <div className="sub num">{formatPhone(t.head.phone)}</div>
            </td>
            <td className="r num" data-col="secondary">
              {t.membersCount}
            </td>
            <td data-col="hide">{t.signal ? <span className="wtx">{SIGNAL_TEXT[t.signal]}</span> : null}</td>
          </tr>
        ))}
      />

      {creating ? <NewTeamModal onClose={() => setCreating(false)} onCreated={(id) => navigate(`/teams/${id}`)} /> : null}
    </div>
  );
}
